/**
 * The shared Central clients (@levelup/central-billing ≥ 1.4.0) over BeHeard's
 * EXISTING browser-direct Central transport (createBeHeardCentralFetcher:
 * Central Bearer from the centralAuth slice; every failure classified by the
 * registered Central-failure handler first). No second auth path, no token
 * storage; Central's status + machine `code` relayed unchanged.
 */
import { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createCentralBillingClient,
  createCentralSettingsClient,
  unwrapCentralResult,
  type CentralBillingClient,
  type CentralResult,
  type CentralSettingsClient,
} from '@levelup/central-billing';
import { selectCentralAccessToken } from '../store/slices/centralAuthSlice';
import { createBeHeardCentralFetcher } from './centralBillingFetcher';
import { refreshSwitchContext } from '../auth/refreshSwitchContext';
import { announceCentralBillingRefresh } from './centralBillingUrls';

export interface CentralClients {
  billing: CentralBillingClient;
  settings: CentralSettingsClient;
}

export function useCentralClients(): CentralClients {
  const token = useSelector(selectCentralAccessToken);
  const tokenRef = useRef(token);
  tokenRef.current = token;
  return useMemo(() => {
    const fetcher = createBeHeardCentralFetcher(() => tokenRef.current);
    return { billing: createCentralBillingClient(fetcher), settings: createCentralSettingsClient(fetcher) };
  }, []);
}

/**
 * After an IN-APP commercial change (plan change, cancelled scheduled change,
 * enabled outcome, rename): the app's EXISTING refresh — re-read switch-context
 * (apps, permissions, plan) and announce a billing re-read (plan card, credit
 * balance) — exactly what a Central billing round trip triggers.
 */
export function useAfterCentralChange(): () => void {
  const dispatch = useDispatch();
  const token = useSelector(selectCentralAccessToken);
  const tokenRef = useRef(token);
  tokenRef.current = token;
  return useCallback(() => {
    void refreshSwitchContext({ dispatch, getToken: () => tokenRef.current });
    announceCentralBillingRefresh();
  }, [dispatch]);
}

/**
 * ONE save (profile / workspace edit) for the shared forms' `onSaveProfile` /
 * `onSaveWorkspace` contract (ui-management ≥ 1.17), on BeHeard's transport:
 *   - Central accepted → `true` (caller refreshes; the form closes)
 *   - a verdict with its own screen was applied (applyRegisteredCentralFailure:
 *     session ended, workspace closed, …) → `false`; nothing is shown in the
 *     form (terminal-auth precedence)
 *   - any other refusal → throws CentralRequestError (Central's message shown in
 *     the form; status + machine code unchanged)
 */
export function useCentralSave(): <T>(run: (settings: CentralSettingsClient) => Promise<CentralResult<T>>) => Promise<boolean> {
  const token = useSelector(selectCentralAccessToken);
  const tokenRef = useRef(token);
  tokenRef.current = token;
  return useCallback(async (run) => {
    let handled = false;
    const base = createBeHeardCentralFetcher(() => tokenRef.current);
    const settings = createCentralSettingsClient(async (path, init) => {
      const res = await base(path, init);
      if (res.handled) handled = true;
      return res;
    });
    const result = await run(settings);
    if (result.ok) return true;
    if (handled) return false;
    unwrapCentralResult(result);
    return false;
  }, []);
}
